import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Server, Wifi, WifiOff, RefreshCw, Activity, Clock,
  CheckCircle, AlertTriangle, XCircle, Monitor, ChevronDown,
  ChevronUp, Cloud, CloudOff, Database, ArrowUpDown, Info
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { hqMonitoringAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const StoreSyncStatus = () => {
  const [syncData, setSyncData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedStores, setExpandedStores] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const response = await hqMonitoringAPI.getStoreSyncStatus();
      setSyncData(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching sync status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const toggleExpanded = (storeId) => {
    setExpandedStores(prev => ({
      ...prev,
      [storeId]: !prev[storeId]
    }));
  };

  const getHealthIcon = (health) => {
    switch (health) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'offline':
        return <CloudOff className="w-5 h-5 text-gray-500" />;
      case 'not_registered':
        return <Info className="w-5 h-5 text-blue-400" />;
      default:
        return <Info className="w-5 h-5 text-gray-400" />;
    }
  };

  const getHealthBadge = (health) => {
    const styles = {
      healthy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-100 text-amber-700 border-amber-200',
      critical: 'bg-red-100 text-red-700 border-red-200',
      offline: 'bg-gray-100 text-gray-600 border-gray-200',
      not_registered: 'bg-blue-50 text-blue-600 border-blue-200',
      unknown: 'bg-gray-50 text-gray-500 border-gray-200'
    };
    const labels = {
      healthy: 'Healthy',
      warning: 'Warning',
      critical: 'Critical',
      offline: 'Offline',
      not_registered: 'Not Registered',
      unknown: 'Unknown'
    };
    return (
      <Badge className={`${styles[health] || styles.unknown} border`}>
        {labels[health] || 'Unknown'}
      </Badge>
    );
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading sync status...</p>
        </div>
      </div>
    );
  }

  const summary = syncData?.summary || {};
  const stores = syncData?.stores || [];

  return (
    <div className="p-6 space-y-6" data-testid="store-sync-status-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Server className="w-7 h-7 text-blue-600" />
            Store Sync Status
          </h1>
          <p className="text-gray-500 mt-1">Monitor local store server connections and sync health</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            Last updated: {formatDate(lastRefresh.toISOString())}
          </div>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs font-medium">Total Stores</p>
                <p className="text-2xl font-bold">{summary.total_stores || 0}</p>
              </div>
              <Database className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs font-medium">Online</p>
                <p className="text-2xl font-bold">{summary.online_stores || 0}</p>
              </div>
              <Wifi className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-500 to-gray-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-200 text-xs font-medium">Offline</p>
                <p className="text-2xl font-bold">{summary.offline_stores || 0}</p>
              </div>
              <WifiOff className="w-8 h-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs font-medium">Healthy</p>
                <p className="text-2xl font-bold">{summary.health_summary?.healthy || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs font-medium">Warning</p>
                <p className="text-2xl font-bold">{summary.health_summary?.warning || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-xs font-medium">Critical</p>
                <p className="text-2xl font-bold">{summary.health_summary?.critical || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store List */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Store Sync Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stores.length === 0 ? (
              <div className="text-center py-12">
                <Server className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No stores found</p>
              </div>
            ) : (
              stores.map((store) => (
                <Collapsible
                  key={store.store_id}
                  open={expandedStores[store.store_id]}
                  onOpenChange={() => toggleExpanded(store.store_id)}
                >
                  <div className={`border rounded-lg overflow-hidden ${
                    store.sync_health === 'healthy' ? 'border-emerald-200' :
                    store.sync_health === 'warning' ? 'border-amber-200' :
                    store.sync_health === 'critical' ? 'border-red-200' :
                    'border-gray-200'
                  }`}>
                    {/* Store Header Row */}
                    <CollapsibleTrigger asChild>
                      <div className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        store.sync_health === 'critical' ? 'bg-red-50' :
                        store.sync_health === 'warning' ? 'bg-amber-50' :
                        ''
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* Status Icon */}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  {getHealthIcon(store.sync_health)}
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Sync Health: {store.sync_health}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            {/* Store Info */}
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-800">{store.store_name}</h3>
                                <span className="text-xs text-gray-500 font-mono">{store.store_code}</span>
                              </div>
                              <p className="text-sm text-gray-500">{store.city}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-6">
                            {/* Online Status */}
                            <div className="flex items-center gap-2">
                              {store.is_online ? (
                                <span className="flex items-center gap-1.5 text-emerald-600">
                                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                                  Online
                                </span>
                              ) : store.is_registered ? (
                                <span className="flex items-center gap-1.5 text-gray-500">
                                  <WifiOff className="w-4 h-4" />
                                  Offline
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 text-blue-500">
                                  <Cloud className="w-4 h-4" />
                                  Not Registered
                                </span>
                              )}
                            </div>

                            {/* Last Sync */}
                            <div className="text-right min-w-[100px]">
                              <p className="text-xs text-gray-500">Last Sync</p>
                              <p className="text-sm font-medium">{getTimeSince(store.last_sync_at)}</p>
                            </div>

                            {/* Terminals */}
                            <div className="flex items-center gap-1 min-w-[60px]">
                              <Monitor className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">{store.terminals_count}/5</span>
                            </div>

                            {/* Health Badge */}
                            {getHealthBadge(store.sync_health)}

                            {/* Expand Icon */}
                            {expandedStores[store.store_id] ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    {/* Expanded Details */}
                    <CollapsibleContent>
                      <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Device Info */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <Server className="w-4 h-4" />
                              Device Information
                            </h4>
                            {store.device_info ? (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Device ID:</span>
                                  <span className="font-mono text-xs">{store.device_info.device_id?.substring(0, 8)}...</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Name:</span>
                                  <span>{store.device_info.device_name}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">IP:</span>
                                  <span className="font-mono">{store.device_info.ip_address || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Registered:</span>
                                  <span>{store.device_info.registered_at ? formatDate(store.device_info.registered_at).split(' ')[0] : 'N/A'}</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm">No device registered</p>
                            )}
                          </div>

                          {/* Sync Stats */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <ArrowUpDown className="w-4 h-4" />
                              Sync Statistics
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Sales Synced:</span>
                                <span className="font-semibold">{store.sync_stats?.total_sales_synced || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Registers Synced:</span>
                                <span className="font-semibold">{store.sync_stats?.total_registers_synced || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Last Inbound:</span>
                                <span>{getTimeSince(store.sync_stats?.last_inbound_sync)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Last Outbound:</span>
                                <span>{getTimeSince(store.sync_stats?.last_outbound_sync)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Terminals */}
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <Monitor className="w-4 h-4" />
                              Terminals ({store.terminals_count}/5)
                            </h4>
                            {store.terminals && store.terminals.length > 0 ? (
                              <div className="space-y-2">
                                {store.terminals.map((terminal) => (
                                  <div key={terminal.terminal_id} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs">{terminal.terminal_id}</span>
                                      <span className="text-gray-500">{terminal.terminal_name}</span>
                                    </div>
                                    {terminal.is_register_open && (
                                      <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">
                                        Register Open
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm">No terminals registered</p>
                            )}
                          </div>
                        </div>

                        {/* Recent Sync Logs */}
                        {store.recent_syncs && store.recent_syncs.length > 0 && (
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Recent Sync Activity
                            </h4>
                            <div className="space-y-2">
                              {store.recent_syncs.slice(0, 5).map((sync, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                                  <div className="flex items-center gap-3">
                                    <Badge 
                                      variant="outline" 
                                      className={`text-xs ${
                                        sync.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                        sync.status === 'partial' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                        'bg-red-50 text-red-600 border-red-200'
                                      }`}
                                    >
                                      {sync.status}
                                    </Badge>
                                    <span className="text-gray-600">{sync.sync_type?.replace('_', ' ')}</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-gray-500">
                                    <span>{sync.processed_count} processed</span>
                                    {sync.failed_count > 0 && (
                                      <span className="text-red-500">{sync.failed_count} failed</span>
                                    )}
                                    <span>{getTimeSince(sync.created_at)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <span className="font-medium text-gray-700">Health Legend:</span>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>Healthy (synced &lt; 1hr)</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span>Warning (synced 1-6hrs)</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <span>Critical (synced &gt; 6hrs)</span>
            </div>
            <div className="flex items-center gap-2">
              <CloudOff className="w-4 h-4 text-gray-500" />
              <span>Offline (not seen &gt; 5min)</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreSyncStatus;
